import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BookIcon, HeadphonesIcon, ShoppingBagIcon, MusicIcon, CalendarIcon, UsersIcon, UserIcon, ImageIcon, StarIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { useBrand } from '../../hooks/useBrand';
import { samplePromotionalGraphics } from '../../data/samplePromotionalGraphics';
import { AuthorHero } from '../../components/shared/AuthorHero';
import { AboutMeTab } from './AboutMeTab';
import { BooksTab } from './BooksTab';
import { GraphicsTab } from './GraphicsTab';
import { CollaborationsTab, AudiobooksTab, MerchandiseTab, PlaylistsTab, EventsTab } from './ContentTabs';
import { authorData, authorBooks, audiobooks, merchandise, playlists, events, similarAuthors } from './data';
const VALID_TABS = ['about', 'books', 'collaborations', 'audiobooks', 'merchandise', 'playlists', 'events', 'graphics'];
export default function AuthorProfile() {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    theme,
    content
  } = useBrand();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTabState] = useState(tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'about');
  const setActiveTab = (tab: string) => {
    setActiveTabState(tab);
    setSearchParams(tab === 'about' ? {} : {
      tab
    }, {
      replace: true
    });
  };
  const tabs = [{
    id: 'about',
    label: 'About',
    icon: UserIcon
  }, {
    id: 'books',
    label: 'Books',
    icon: BookIcon,
    count: authorData.booksPublished
  }, {
    id: 'collaborations',
    label: 'Co-Writes',
    icon: UsersIcon,
    count: 1
  }, {
    id: 'audiobooks',
    label: 'Audiobooks',
    icon: HeadphonesIcon,
    count: 2
  }, {
    id: 'merchandise',
    label: 'Merch',
    icon: ShoppingBagIcon,
    count: 2
  }, {
    id: 'playlists',
    label: 'Playlists',
    icon: MusicIcon,
    count: 2
  }, {
    id: 'events',
    label: 'Events',
    icon: CalendarIcon,
    count: 3
  }, {
    id: 'graphics',
    label: 'Graphics',
    icon: ImageIcon
  }];
  const authorGraphics = samplePromotionalGraphics.filter(graphic => graphic.authorId === 'elena-richards');
  const featuredBooks = [...authorBooks.standalone.slice(0, 2).map(book => ({
    ...book,
    seriesName: '',
    seriesNumber: 0,
    spiceLevel: book.heatLevel || 0,
    series: undefined
  })), ...(authorBooks.series[0]?.books || []).slice(0, 2).map(book => ({
    ...book,
    seriesName: 'Echo Series',
    seriesNumber: (book as {
      seriesPosition?: number;
    }).seriesPosition || 0,
    spiceLevel: book.heatLevel || 0,
    series: undefined
  }))];
  return <div className={`min-h-screen ${dk.page} ${dk.textPrimary}`} data-id="element-6500" data-mp="3mbgq5b6957g4">
      <AuthorHero name={authorData.name} avatar={authorData.avatar} backdrop={authorData.aestheticImages[0]} location={authorData.location} status="Open for Events" genres={authorData.genres} bio={authorData.bio} stats={[{
      value: authorData.rating,
      label: 'Rating',
      star: true
    }, {
      value: authorData.booksPublished,
      label: 'Books'
    }, {
      value: authorData.seriesCount,
      label: 'Series'
    }, {
      value: authorData.followers,
      label: 'Followers'
    }, {
      value: '$1.2k',
      label: 'Donated',
      highlight: true
    }]} data-id="element-6501" data-mp="3maxa6005wt4q" />

      {/* Tabs Navigation */}
      <div className={`sticky top-0 z-30 border-b backdrop-blur-sm ${dk.navTranslucent} ${dk.divider}`} data-id="element-6502" data-mp="7o9gsnfgjmna">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6503" data-mp="cljhx0c7lmqy">
          <div className="flex overflow-x-auto scrollbar-hide" role="tablist" data-id="element-6504" data-mp="10covt9yla4ym">
            {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return <button key={tab.id} role="tab" aria-selected={isActive} onClick={() => setActiveTab(tab.id)} className={`relative flex items-center gap-2 py-4 px-6 text-sm font-medium whitespace-nowrap transition-colors ${isActive ? rt.textPrimary : `${dk.textMuted} ${rt.textHover}`}`} data-id="element-6505" data-mp="3cnrnufjhrzpv">
                  <Icon className="w-4 h-4" data-id="element-6506" data-mp="4a2hprwy6r9" />
                  {tab.label}
                  {tab.count !== undefined && <span className={`ml-1.5 text-xs py-0.5 px-2 rounded-full ${isActive ? rt.badge : dk.badgeNeutral}`} data-id="element-6507" data-mp="3rceg1hu7wlyi">
                      {tab.count}
                    </span>}
                  {isActive && <motion.div layoutId="activeAuthorTab" className={`absolute bottom-0 left-0 right-0 h-0.5 ${rt.bgDark}`} data-id="element-6508" data-mp="iwl0b57pneii" />}
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24" data-id="element-6509" data-mp="qherdnhfn1o0">
        <AnimatePresence mode="wait" data-id="element-6510" data-mp="vdm1ztkrx1ca">
          <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} data-id="element-6511" data-mp="vgror25xjr4u">
            {activeTab === 'about' && <AboutMeTab data-id="element-6512" data-mp="19cvdify1b79x" />}
            {activeTab === 'books' && <BooksTab books={authorBooks} data-id="element-6513" data-mp="1e56g5qk5t766" />}
            {activeTab === 'collaborations' && <CollaborationsTab collaborations={authorBooks.collaborations} data-id="element-6514" data-mp="1j3q39uahop04" />}
            {activeTab === 'audiobooks' && <AudiobooksTab audiobooks={audiobooks} authorName={authorData.name} data-id="element-6515" data-mp="1o4dscaf5qvrp" />}
            {activeTab === 'merchandise' && <MerchandiseTab merchandise={merchandise} data-id="element-6516" data-mp="3p6dcbjq1eez1" />}
            {activeTab === 'playlists' && <PlaylistsTab playlists={playlists} data-id="element-6517" data-mp="3k7sa5er85l7y" />}
            {activeTab === 'events' && <EventsTab events={events} data-id="element-6518" data-mp="1t19e96i32dp" />}
            {activeTab === 'graphics' && <GraphicsTab authorId="elena-richards" authorName={authorData.name} graphics={authorGraphics} books={featuredBooks} data-id="element-6519" data-mp="3t5uikfdafg59" />}
          </motion.div>
        </AnimatePresence>

        {/* Similar Authors */}
        <section className={`mt-20 pt-10 border-t ${dk.divider}`} data-id="element-6520" data-mp="kq2gbzjm7cka">
          <h2 className={`text-xl font-bold mb-6 ${dk.textHeading}`} data-id="element-6521" data-mp="gprzfruab1qn">
            Similar Authors
          </h2>
          <div className="flex overflow-x-auto gap-6 pb-4 scrollbar-hide" data-id="element-6522" data-mp="2mhxedccy7ik1">
            {similarAuthors.map(author => <Link key={author.id} to={content.routes.author(author.name)} className={`flex-shrink-0 w-64 p-4 rounded-xl border transition-shadow hover:shadow-md ${dk.card}`} data-id="element-6523" data-mp="2ii6odav8x6gk">
                <div className="flex items-center gap-4 mb-3" data-id="element-6524" data-mp="2cgctpzjxmc0z">
                  <img src={author.avatar} alt={author.name} className="w-14 h-14 rounded-full object-cover" data-id="element-6525" data-mp="1ymlw9pjh6ilm" />
                  <div data-id="element-6526" data-mp="1oo63zj8pp3aj">
                    <h3 className={`font-bold text-sm ${dk.textPrimary}`} data-id="element-6527" data-mp="1onmlo1f5gcvz">
                      {author.name}
                    </h3>
                    <div className="flex items-center gap-1 text-xs text-amber-500" data-id="element-6528" data-mp="1foizupolljds">
                      <StarIcon className="w-3 h-3 fill-current" data-id="element-6529" data-mp="1kncg2pv9rohn" />
                      <span data-id="element-6530" data-mp="1knvw211d02t1">{author.rating}</span>
                    </div>
                  </div>
                </div>
                <div className={`flex justify-between items-center text-xs ${dk.textMuted}`} data-id="element-6531" data-mp="3a07mle12mq8q">
                  <span data-id="element-6532" data-mp="rg54abjpjg5b">{author.books} Books</span>
                  <span className={`px-2 py-0.5 ${theme.tagRadius} ${dk.badgeNeutral}`} data-id="element-6533" data-mp="rfn26yad67ph">
                    {author.genre}
                  </span>
                </div>
              </Link>)}
          </div>
        </section>
      </div>
    </div>;
}